import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { usePrevious } from 'bxcommon/hooks/usePrevious';
import { BaseLoaderSpinner } from 'bxcommon';
import UserAccountConfirmation from 'bxcommon/components/UserAccountConfirmation/UserAccountConfirmation';
import UserRegistrationConfirmation from 'bxcommon/components/UserRegistrationConfirmation/UserRegistrationConfirmation';
import UserForgotPassword from 'bxcommon/components/UserForgotPassword/UserForgotPassword';
import UserResetPassword from 'bxcommon/components/UserResetPassword/UserResetPassword';
import DocumentDownload from 'bxcommon/components/DocumentDownload/DocumentDownload';
import PrivateRoutes from 'bxcommon/components/RouteWrappers/PrivateRoutes';
import UnauthenticatedRoutes from 'bxcommon/components/RouteWrappers/UnauthenticatedRoutes';
import { RequestProvider } from 'bxcommon/context/RequestProvider';
import { initExistingToken } from 'bxcommon/store/slices/user.slice';
import { warningToast } from 'bxcommon/store/slices/toaster.slice';
import { api } from 'bxcommon/store/api';
import { hydrate as hydrateResources } from 'bxcommon/store/slices/resources.slice';
import authHelper from 'bxcommon/helpers/auth.helper';

import ListView from './components/ListView/ListView';
import ProspectusDetails from './components/ProspectusDetails/ProspectusDetails';
import RequestView from './components/RequestView/RequestView';
import UserRegistration from './components/UserRegistration/UserRegistration';
import UserLogin from './components/UserLogin/UserLogin';
import AdminLogin from './components/AdminLogin/AdminLogin';
import UserGroupsList from './components/UserGroupsList/UserGroupsList';
import UserGroupsEdit from './components/UserGroupsEdit/UserGroupsEdit';
import FinalTerms from './components/FinalTerms/FinalTerms';
import Addendum from './components/Addendum/Addendum';

export const RouteList = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const location = useLocation();

  // Redux state selectors
  const resources = useSelector((state) => state.resources);
  const user = useSelector((state) => state.user);

  const [loaded, setLoaded] = useState(false);
  const previousLocation = usePrevious(location.pathname);

  // Effect to handle initial app loading
  useEffect(() => {
    const initializeApp = async () => {
      // Things need to be loaded for app to work
      if (!Object.keys(resources).length) {
        try {
          const resourcesResult = await dispatch(api.endpoints.getResources.initiate('/prospectuses/resources/'));
          if (resourcesResult.data) {
            dispatch(hydrateResources(resourcesResult.data));
          }

          // if we have token and it's not expired set login action to true (create login only action)
          if (!user.loggedIn && !authHelper.isExpired) {
            await dispatch(initExistingToken());
          } else if (authHelper.token && authHelper.isExpired) {
            warningToast(t('errors.sessionExpired'));
            authHelper.removeToken();
          }
        } finally {
          setLoaded(true);
        }
      }
    };

    initializeApp();
  }, [resources, user.loggedIn, dispatch, t]);

  // Effect to scroll to top on route change
  useEffect(() => {
    if (location.pathname !== previousLocation) {
      setTimeout(() => window.scrollTo(0, 0), 0);
    }
  }, [location.pathname, previousLocation]);

  if (!loaded) {
    return <BaseLoaderSpinner large />;
  }

  return (
    <RequestProvider url="prospectuses">
      <Routes>
        <Route element={<PrivateRoutes />}>
          <Route path="/list" element={<ListView />} />
          <Route path="/details/:detailsId" element={<ProspectusDetails />} />
          <Route path="/request">
            <Route path="equity/:requestId?" element={<RequestView type="equity" />} />
            <Route path="debt/:requestId?" element={<RequestView type="debt" />} />
            <Route path="derivative/:requestId?" element={<RequestView type="derivative" />} />
          </Route>
          <Route path="/groups/edit/:groupId?" element={<UserGroupsEdit />} />
          <Route path="/groups" element={<UserGroupsList />} />
          <Route path="/final-terms/:requestId?" element={<FinalTerms />} />
          <Route path="/addendum/:requestId?" element={<Addendum />} />
          <Route path="/documents/:uuid" element={<DocumentDownload />} />
          <Route path="/prospectuses/final-terms/:uuid" element={<DocumentDownload />} />
          <Route path="/prospectuses/addenda/:uuid" element={<DocumentDownload />} />
        </Route>

        <Route element={<UnauthenticatedRoutes />}>
          <Route path="/register/confirmation" element={<UserRegistrationConfirmation />} />
          <Route path="/register" element={<UserRegistration />} />
          <Route path="/login" element={<UserLogin />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/forgot-password" element={<UserForgotPassword />} />
          <Route path="/reset-password" element={<UserResetPassword />} />
          <Route path="/confirm-account" element={<UserAccountConfirmation />} />
        </Route>

        <Route path="/" element={<Navigate to="/list" replace />} />
        <Route path="*" element={<Navigate to="/list" replace />} />
      </Routes>
    </RequestProvider>
  );
};

export default RouteList;
